/**
 * Shared validation for filter parameters (date range, branches, etc.).
 * Used by clarity, clarityLens, targets and other modules so that invalid params
 * never trigger API calls. Waiting for params to become valid is part of "loading".
 */

export interface FilterValidation {
  isValid: boolean
  reason?: string
}

/**
 * Validates date params. startDate is required; endDate is optional (single-day
 * selection may set only startDate, with endDate empty, undefined or null).
 */
export function validateDateParams(
  startDate: string | undefined | null
): FilterValidation {
  if (
    startDate === undefined ||
    startDate === null ||
    String(startDate).trim() === ''
  ) {
    return { isValid: false, reason: 'startDate is required' }
  }
  // endDate optional: allow undefined, null, or empty for single-day range
  return { isValid: true }
}

export function validateBranchParams(
  branches: string | undefined | null
): FilterValidation {
  if (branches === undefined || branches === null) {
    return { isValid: false, reason: 'branches is required' }
  }
  if (typeof branches === 'string' && branches.trim() === '') {
    return { isValid: false, reason: 'branches cannot be empty' }
  }
  return { isValid: true }
}

export interface FilterParams {
  startDate?: string | null
  endDate?: string | null
  branches?: string | null
}

/**
 * Validates the common filter set: startDate (required), endDate (optional), branches (required when used).
 * Use when a hook requires date range and optionally branches.
 */
export function validateFilterParams(params: FilterParams): FilterValidation {
  const dateResult = validateDateParams(params.startDate)
  if (!dateResult.isValid) return dateResult
  const branchResult = validateBranchParams(params.branches)
  if (!branchResult.isValid) return branchResult
  return { isValid: true }
}

/**
 * For hooks that only need date range (e.g. region box metrics that get branches elsewhere).
 * Valid when startDate is present; endDate may be empty for single-day selection.
 */
export function areDateParamsValid(
  startDate: string | undefined | null
): boolean {
  return validateDateParams(startDate).isValid
}

/**
 * For hooks that need date range + branches (e.g. clarity metrics, clarityLens, targets).
 */
export function areFilterParamsValid(
  startDate: string | undefined | null,
  endDate: string | undefined | null,
  branches: string | undefined | null
): boolean {
  return validateFilterParams({
    startDate,
    endDate,
    branches,
  }).isValid
}

/** Alias for clarity hooks; same as areFilterParamsValid. */
export const areClarityParamsValid = areFilterParamsValid

/** DateRange from react-day-picker: { from?: Date; to?: Date }. to is optional (single-day = only from). */
export function areDateRangeAndBranchesValid(
  date: { from?: Date; to?: Date } | undefined,
  branches: string | undefined | null
): boolean {
  if (!date?.from) return false
  const startDate = date.from.toISOString().slice(0, 10)
  return (
    validateDateParams(startDate).isValid &&
    validateBranchParams(branches).isValid
  )
}
